import { useEffect, useState } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { ADMIN_PAGE_SIZE, getAdminOperations } from "../lib/api";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { PageHeading } from "./PageHeading";
import { SkeletonRows } from "./Skeleton";
import { TableEmpty } from "./TableEmpty";
import type { AdminOperation } from "../types";
import { PaginationControls } from "./PaginationControls";

export function AuditPage() {
  const { timeZone } = useTimeZone();
  const [operations, setOperations] = useState<AdminOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    setLoading(true); setError(null);
    void getAdminOperations(cursors[pageIndex] ?? undefined)
      .then((result) => { setOperations(result.operations); setNextCursor(result.nextCursor); })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Unable to load audit trail"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [pageIndex]);

  return <div className="page-stack">
    <PageHeading
      eyebrow="OPERATIONS"
      title="Audit trail"
      description="Immutable gateway operation records for privileged portal actions."
      info={{
        label: "audit sources",
        children: <><strong>Cross-service operation log.</strong> SQL fee history remains authoritative for committed pricing changes.</>
      }}
      actions={<button className="secondary-button" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? "spin" : undefined} />Refresh</button>}
    />
    {error ? <div className="error-panel"><AlertCircle size={19} /><div><strong>Audit trail unavailable</strong><p>{error}</p></div></div> : null}
    <section className="history-card"><div className="section-heading-row"><h2>Recent operations</h2><span className="history-count">{loading && operations.length === 0 ? "Loading…" : `${operations.length} shown`}</span></div><div className="table-wrap"><table aria-busy={loading}><thead><tr><th>Requested</th><th>Action</th><th>Actor</th><th>Target</th><th>State</th><th>Reason</th></tr></thead><tbody>
      {loading && operations.length === 0 ? <SkeletonRows columns={6} /> : null}
                {!loading && operations.length === 0 ? <TableEmpty columns={6} filtered={false} emptyMessage="No portal operations recorded yet" /> : null}
      {operations.map((operation) => <tr key={operation.id}><td>{formatTimestamp(operation.requestedAt, timeZone, { dateStyle: "medium", timeStyle: "medium" }, "Pending timestamp")}</td><td><strong className="cell-title">{operation.action}</strong><span className="cell-subtitle mono-cell">{operation.id}</span></td><td>{operation.actorEmail || "—"}<span className="cell-subtitle">{operation.actorRole || "—"}</span></td><td>{operation.targetType || "—"}<span className="cell-subtitle mono-cell">{operation.targetId || "—"}</span></td><td><span className={`status-badge status-${operation.state}`}>{operation.state}</span></td><td className="wrap-cell">{operation.reason || "—"}</td></tr>)}
    </tbody></table></div><PaginationControls page={pageIndex + 1} pageSize={ADMIN_PAGE_SIZE} count={operations.length} hasNext={Boolean(nextCursor)} loading={loading} onPrevious={() => setPageIndex((value) => Math.max(0, value - 1))} onNext={() => { if (!nextCursor) return; setCursors((current) => [...current.slice(0, pageIndex + 1), nextCursor]); setPageIndex((value) => value + 1); }} /></section>
  </div>;
}
